(ns ductile.dependent-selects
  (:require [ductile.ui.locale :refer [tr]]))

(defn valid-selection? [choice-tree selection]
  (or
   (and (empty? choice-tree) (empty? selection))
   (some (fn [[k v]]
           (let [possible-values (choice-tree k)]
             (cond (map? possible-values) (and (contains? possible-values v)
                                               (valid-selection? (get possible-values v) (dissoc selection k)))
                   :else (and (contains? (set possible-values) v)
                              (empty? (dissoc selection k)))))) selection)))

(valid-selection? {:shelf/row {"A" {:shelf/place {2 {}
                                                  3 {:shelf/level [2]}}}
                               "B" {:shelf/level [1 5]}}}
                  {:shelf/row "A" :shelf/place 2})

(valid-selection? {:shelf/row {"A" {:shelf/place {2 {}
                                                  3 {:shelf/level [2]}}}
                               "B" {:shelf/level [1 5]}}}
                  {:shelf/row "A" :shelf/place 3 :shelf/level 2})

(not (valid-selection? {:shelf/row {"A" {:shelf/place [2 3]}
                                    "B" {:shelf/level [1 5]}}}
                       {:shelf/row "B" :shelf/place 3}))

(not (valid-selection? {:shelf/row {"A" {:shelf/place [2 3]}
                                    "B" {:shelf/level [1 5]}}}
                       {:shelf/row "B" :shelf/level 3}))

(not (valid-selection? {:shelf/row {"A" {:shelf/place [2 3]}
                                    "B" {:shelf/level [1 5]}}}
                       {:shelf/row "B" :shelf/level 3}))

(valid-selection? {:shelf/row {"A" {:shelf/place [2 3]}
                               "B" {:shelf/level [1 5]}}}
                  {:shelf/row "B" :shelf/level 5})

(valid-selection? {:shelf/row {"A" {:shelf/place [2 3]}
                               "B" {:shelf/level [1 5]}}}
                  {:shelf/row "B" :shelf/level 5})


(defn filter-ignored-entries [m ignore-key?]
  (into {} (remove (fn [[k _]] (when (fn? ignore-key?) (ignore-key? k))) m)))

(defn sort-options [opts]
  (let [strings (filter string? opts)
        keywords (filter keyword? opts)
        numbers (filter number? opts)
        others (remove (fn [x] (or (string? x) (keyword? x) (number? x))) opts)]
    (vec (concat (sort strings)
                 (sort-by name keywords)
                 (sort numbers)
                 (sort-by str others)))))

(defn options-from-map-keys [m]
  (sort-options (keys m)))

(defn build-levels-indexed
  "Level builder that threads an index through the traversal.
   - root        => nested data
   - getsel      => (fn [kw idx] => [sel consumed?])
                    Return the selected value for `kw` (or nil), and whether the
                    selection *consumes* one position of idx. This lets us unify
                    map- and vector-driven selection models.
   - ignore-key? => predicate to filter out non-UI keys
   - idx0        => starting index (usually 0)

   Returns [levels idx1], where levels is a vector of
   {:group-key kw-or-nil :label string :options [...]} and idx1 is the updated index."
  [root getsel ignore-key? idx0]
  (letfn [(step [node current-kw acc idx]
            (cond
              (or (set? node) (vector? node))
              [(conj acc {:kind :leaf
                          :group-key current-kw
                          :label (tr (or current-kw "Select"))
                          :options (sort-options node)})
               idx]

              (map? node)
              (let [m (filter-ignored-entries node ignore-key?)
                    cnt (count m)]
                (cond
                  (zero? cnt) [acc idx]

                  (and (= 1 cnt) (keyword? (-> m first key)))
                  (let [[k v] (first m)]
                    (step v k acc idx))

                  :else
                  (let [opts  (options-from-map-keys m)
                        level {:kind :choices
                               :group-key current-kw
                               :label (tr (or current-kw "Select"))
                               :options opts
                               :children m}
                        acc' (conj acc level)
                        [sel consumed?] (if current-kw (getsel current-kw idx) [nil false])
                        idx' (if consumed? (inc idx) idx)]
                    (if (some? sel)
                      (step (get m sel) nil acc' idx')
                      [acc' idx']))))

              :else [acc idx]))]
    (let [[levels idx1] (step root nil [] idx0)]
      [(->> levels (map #(dissoc % :children)) (remove nil?) vec)
       idx1])))

(defn build-levels
  "Convenience wrapper when you don't care about the resulting index.
   `getsel` is the same as in `build-levels-indexed`."
  [root getsel ignore-key?]
  (first (build-levels-indexed root getsel ignore-key? 0)))

(defn levels-from-root
  "Map-driven selection model (flat map): idx is never consumed."
  [root value-map ignore-key?]
  (build-levels root (fn [kw _idx] [(get value-map kw) false]) ignore-key?))

(defn levels-from-selections
  "Vector-driven selection model: each rendered level consumes at most one idx
   when there is a selection present."
  [root selections ignore-key?]
  (let [getsel (fn [_kw idx]
                 (let [v (nth selections idx nil)]
                   [v (some? v)]))]
    (first (build-levels-indexed root getsel ignore-key? 0))))
